import React, { useEffect, useState } from "react";
import { usePackages } from "../../../../../hooks/usePackages";
import {
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
  CartesianGrid,
  AreaChart,
  Area,
} from "recharts";
import { handleMonth, translate } from "../../../../../utils/Common";
import Spinner from "../../../../../components/Spinner";
import { LuBox } from "react-icons/lu";
import { FaCalendarAlt } from "react-icons/fa";
import PlatformSearchList from "../../../../../components/PlatformSearchList";
import { useForm } from "react-hook-form/dist/index.ie11";
import Select from "../../../../../components/common/Select";
import styles from "./Charts.module.css";

const PackagesPickupByDay = (props) => {
  const { yearSelect, canReadFilters = true } = props;
  const { getPickupByDay } = usePackages();
  const { register, errors, setValue } = useForm();

  const [data, setData] = useState(null);
  const [monthSelect, setMonthSelect] = useState(null);
  const [platformSelect, setPlatformSelect] = useState(null);
  const [typePackage, setTypePackage] = useState(2);
  const [isLoading, setIsLoading] = useState(false);

  const getData = async () => {
    setIsLoading(true);
    const signal = new AbortController().signal;
    const result = await getPickupByDay(signal, {
      year: Number(yearSelect),
      month: Number(monthSelect),
      lockerId: platformSelect?.boxitLocationId ? Number(platformSelect?.boxitLocationId) : null,
      typePackage: Number(typePackage)
    });
    setIsLoading(false);

    if (result.length > 0) {
      const formattedData = result.map((item) => ({
        day: item?.dayCurrent,
        total: item?.result,
      }));
      setData(formattedData);
    } else {
      setData([]);
    }
  };

  useEffect(() => {
    if (yearSelect !== null && monthSelect !== null) {
      getData();
    }
  }, [yearSelect, monthSelect, platformSelect, typePackage]);

  useEffect(() => {
    const date = new Date();
    setMonthSelect(date.getMonth() + 1);
  }, []);

  const typePackageOptions = [
    { value: "2", label: translate("global.status.typePackage.regular") },
    { value: "3", label: translate("global.status.typePackage.returned") },
  ];

  const monthOptions = Array.from({ length: 12 }, (_, i) => i + 1).map((m) => ({
    value: String(m),
    label: handleMonth(m, "L"),
  }));

  return (
    <div className={styles.chartSection}>
      <div className={styles.chartHeaderRow}>
        <h6 className={styles.chartTitle}>
          {isLoading ? <span><Spinner type={1} /></span> : <LuBox size={20} aria-hidden />}{" "}
          {translate("global.packagesPickupXDay")} -{" "}
          <span className={styles.chartTitleAccent}>
            {monthSelect && handleMonth(monthSelect, "L")} {yearSelect}
          </span>
        </h6>
        {canReadFilters && (
          <div className={styles.filterGroup}>
            <label htmlFor="inputTypePackage" className={styles.filterLabel}>
              <LuBox size={12} /> {translate("global.typeOfPackage")}
            </label>
            <Select
              id="inputTypePackage"
              value={String(typePackage)}
              onChange={(e) => setTypePackage(Number(e.target.value))}
              options={typePackageOptions}
              size="sm"
            />
          </div>
        )}
      </div>

      <hr className={styles.chartDivider} />

      {canReadFilters && (
        <div className={styles.chartFilters}>
          <div className={styles.filterGroup}>
            <label htmlFor="inputMonth" className={styles.filterLabel}>
              <FaCalendarAlt size={12} /> {translate("global.selectAMonth")}
            </label>
            <Select
              id="inputMonth"
              value={String(monthSelect || "")}
              onChange={(e) => setMonthSelect(e.target.value ? Number(e.target.value) : null)}
              options={monthOptions}
              placeholder={translate("global.select")}
              size="sm"
            />
          </div>
          <div className={styles.selectWrapper} style={{ width: "250px" }}>
            <PlatformSearchList
              register={register}
              errors={errors}
              setPlatformSelect={setPlatformSelect}
              platfSelect={platformSelect}
              valueId={"plataforma"}
              readOnly={false}
              setValue={setValue}
              isClearable
            />
          </div>
        </div>
      )}

      {(yearSelect && monthSelect) !== null ? (
        data === null ? null : data.length === 0 ? (
          <div className={styles.noDataCard}>{translate("global.noDataToView")}</div>
        ) : (
          <div className={styles.chartContainerTall}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border-light)" />
                <XAxis dataKey="day" stroke="var(--text-secondary)" />
                <YAxis stroke="var(--text-secondary)" />
                <Tooltip
                  labelFormatter={(label) => `${label} ${handleMonth(monthSelect, "L")} ${yearSelect}`}
                />
                <Legend />
                <Area
                  type="monotone"
                  dataKey="total"
                  stroke="var(--color-primary)"
                  fill="var(--color-primary)"
                  fillOpacity={0.3}
                  name={translate("global.packages")}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )
      ) : null}
    </div>
  );
};

export default PackagesPickupByDay;
